import React, { useEffect, useMemo, useState } from 'react'
import { Menu, Layout, theme } from 'antd'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { MenuProps } from 'antd'
import layersService from '../../services/layersService'
import PageShell from '../../components/PageShell/PageShell'

type Props = {
    role: number
}
type MenuItem = Required<MenuProps>['items'][number];

type MenuItemMeta = {
  key?: string;
  label?: string;
  type?: 'group' | 'divider';
  children?: MenuItemMeta[];
  roles?: number[];
}

const filterMenuByRole = (items: MenuItemMeta[], role: number): MenuItemMeta[] =>
  items
    .filter(item => !item.roles || item.roles.includes(role))
    .map(item => ({
      ...item,
      children: item.children ? filterMenuByRole(item.children, role) : undefined
    }))
    .filter(item => item.children ? item.children.length > 0 : true)

// Leaf keys only: groups and dividers carry no key and cannot be selected.
const collectKeys = (items: MenuItemMeta[]): string[] =>
  items.flatMap((item) => {
    if (item.children?.length) {
      return collectKeys(item.children)
    }
    return item.key ? [item.key] : []
  })

const AdminLayout: React.FC<Props> = ({ role }) => {
  const [objs, setObjs] = useState<MenuItemMeta[]>([])
  const fetchData = () => {
    layersService.list()
      .then((r) => {
        setObjs(r.layers.map((l) => ({
          key: `/objs/${l.id}`, label: l.name, roles: [2, 9]
        })))
      })
  }

  useEffect(() => {
    fetchData()
  }, [])

  const navigate = useNavigate()

  const getRoleMenu = (role: number): MenuItemMeta[] => {
    const menu: MenuItemMeta[] = [
      {
        label: 'Аналитика',
        type: 'group',
        children: [
          { key: '/categories', label: 'Категории', roles: [9] },
          { key: '/services', label: 'Сервисы', roles: [9] },
          { key: '/researches', label: 'Исследования', roles: [2, 9] }
        ]
      },
      {
        label: 'Объекты',
        type: 'group',
        children: [
          { key: '/data', label: 'Карта', roles: [2, 9] },
          ...objs
        ]
      },
      {
        label: 'Данные',
        type: 'group',
        children: [
          { key: '/domains', label: 'Домены', roles: [9] },
          { key: '/layers', label: 'Слои', roles: [2, 9] },
          { key: '/attribute-numbers', label: 'Числа', roles: [2, 9] },
          { key: '/attribute-texts', label: 'Тексты', roles: [2, 9] },
          { key: '/attribute-dictionaries', label: 'Словари', roles: [2, 9] },
          { key: '/import', label: 'Импорт', roles: [9] }
        ]
      },
      { type: 'divider', roles: [9] },
      { key: '/users', label: 'Пользователи', roles: [9] },
      { key: '/pages', label: 'Страницы', roles: [9] },
      { key: '/parser', label: 'Парсер', roles: [2, 9] }
    ]

    return filterMenuByRole(menu, role)
  }

  const location = useLocation()
  const { token } = theme.useToken()

  const menuItems = useMemo(() => getRoleMenu(role), [role, objs])

  // /data fills the whole content area with the map, so it must not be wrapped
  // in the padded white surface every other page gets.
  const isMapRoute = location.pathname === '/data' || location.pathname.startsWith('/data/')

  // Nested routes share prefixes, so the most specific match wins:
  // /pages/edit/7 highlights "Страницы", never anything shorter.
  const selectedKey = useMemo(() => {
    const pathname = location.pathname

    return collectKeys(menuItems)
      .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
      .sort((a, b) => b.length - a.length)[0]
  }, [menuItems, location.pathname])

  return (
        <Layout.Content style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <PageShell
                sider={
                    <Layout.Sider
                        width={200}
                        style={{ background: token.colorBgContainer }}
                    >
                        <Menu
                            mode="inline"
                            onClick={(e) => navigate(e.key)}
                            selectedKeys={selectedKey ? [selectedKey] : []}
                            items={menuItems as MenuItem[]}
                        />
                    </Layout.Sider>
                }
                plain={isMapRoute}
            >
              <Outlet/>
            </PageShell>
        </Layout.Content>
  )
}

export default AdminLayout
